import type { ContactAddress } from '@rsmaids/shared/types/contacts';

// Zoho's own address shape on a quote (billing_address / shipping_address).
export interface QuoteAddress {
  address?: string;
  street2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

const COUNTRY_NAMES: Record<string, string> = { // i18n-ignore: sent to Zoho, stays English
  US: 'United States', CA: 'Canada', GB: 'United Kingdom', AU: 'Australia', MX: 'Mexico', NZ: 'New Zealand',
};

export function contactAddressToQuote(a: ContactAddress): QuoteAddress {
  const sub = a.address.subdivision ?? '';
  const country = a.address.country ?? '';
  return {
    address: a.address.addressLine1 || undefined,
    street2: a.address.addressLine2 || undefined,
    city: a.address.city || undefined,
    state: a.address.state || (sub.includes('-') ? sub.split('-').pop() : sub) || undefined,
    zip: a.address.postalCode || undefined,
    country: COUNTRY_NAMES[country.toUpperCase()] ?? (country || 'United States'), // i18n-ignore: sent to Zoho
  };
}

export function formatQuoteAddress(a: QuoteAddress): string {
  const cityLine = [a.city, [a.state, a.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [a.address, a.street2, cityLine].filter(Boolean).join(', ');
}

// A contact often holds the same address twice (e.g. a billing and a home entry) — list it once.
export function uniqueQuoteAddresses(list: ContactAddress[]): QuoteAddress[] {
  const seen = new Set<string>();
  const out: QuoteAddress[] = [];
  for (const a of list) {
    const q = contactAddressToQuote(a);
    if (!q.address && !q.city) continue;
    const key = `${(q.address ?? '').toLowerCase()}|${(q.city ?? '').toLowerCase()}|${q.zip ?? ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(q);
  }
  return out;
}
